// src/ForgotPassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import logo from './assets/SnapConX-icon.png';
import { FiMail, FiArrowLeft, FiCheck } from 'react-icons/fi';
import './ForgotPassword.css';
import { auth } from './config/firebase';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccessMessage('Password reset email sent! Check your inbox.');
      setEmail('');
      
      // Optionally redirect to login after a few seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (error) {
      console.error('Password reset error:', error.code, error.message);
      switch (error.code) {
        case 'auth/user-not-found':
          setErrorMessage('No account found with this email address.');
          break;
        case 'auth/invalid-email':
          setErrorMessage('Invalid email format.');
          break;
        case 'auth/too-many-requests':
          setErrorMessage('Too many requests. Please try again later.');
          break;
        default:
          setErrorMessage('Failed to send reset email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-content">
          {/* Left Side - Branding */}
          <div className="forgot-password-branding">
            <div className="branding-inner">
              <img src={logo} alt="SnapConX logo" className="forgot-password-logo" />
              <h1 className="forgot-password-app-name">SnapConX</h1>
              <p className="forgot-password-tagline">Secure Password Recovery</p>
              <div className="recovery-steps">
                <div className="step-item">
                  <span className="step-number">1</span>
                  <span>Enter your email</span>
                </div>
                <div className="step-item">
                  <span className="step-number">2</span>
                  <span>Check your inbox</span>
                </div>
                <div className="step-item">
                  <span className="step-number">3</span>
                  <span>Reset your password</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Reset Form */}
          <div className="forgot-password-form-section">
            <div className="form-card">
              <button
                className="back-button"
                onClick={() => navigate('/login')}
                aria-label="Back to login"
              >
                <FiArrowLeft />
                <span>Back to Login</span>
              </button>

              <h2 className="forgot-password-title">Forgot Password?</h2>
              <p className="forgot-password-subtitle">
                No worries! Enter your email and we'll send you reset instructions.
              </p>

              <form onSubmit={handleResetPassword} className="reset-form">
                <div className="input-group">
                  <label htmlFor="email">Email Address</label>
                  <div className="input-container">
                    <FiMail className="input-icon" />
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={e => { 
                        setEmail(e.target.value); 
                        setErrorMessage(''); 
                        setSuccessMessage('');
                      }}
                      className="form-input"
                      autoComplete="email"
                      disabled={loading}
                    />
                  </div>
                </div>

                {errorMessage && (
                  <div className="error-message">
                    <span className="error-icon">⚠</span>
                    {errorMessage}
                  </div>
                )}

                {successMessage && (
                  <div className="success-message">
                    <FiCheck className="success-icon" />
                    {successMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="reset-button"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Sending...
                    </>
                  ) : (
                    'Send Reset Link'
                  )}
                </button>
              </form>

              <div className="help-text">
                <p>Didn't receive the email? Check your spam folder or try again.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}